import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';

const ChartCard1 = ({ loadingTime, lastLoaded, data, timeframe, currentFrames = 100, onFetchFrames, isNscChart = false, nscAdv = 0,nscDec = 0,nscNotcha = 0 }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null); 
  
  const seriesRefs = useRef({ sma20: null, sma50: null, sma200: null });
  const [framesInput, setFramesInput] = useState(currentFrames);

  const [visibility, setVisibility] = useState({
    sma20: true,
    sma50: true,
    sma200: true
  });

  useEffect(() => {
    setFramesInput(currentFrames);
  }, [currentFrames]);

  useEffect(() => {
    if (seriesRefs.current.sma20) seriesRefs.current.sma20.applyOptions({ visible: visibility.sma20 });
    if (seriesRefs.current.sma50) seriesRefs.current.sma50.applyOptions({ visible: visibility.sma50 });
    if (seriesRefs.current.sma200) seriesRefs.current.sma200.applyOptions({ visible: visibility.sma200 });
  }, [visibility]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    chartContainerRef.current.innerHTML = '';

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { type: 'solid', color: '#000000' },
        textColor: '#7c8a9e', 
      },
      grid: {
        vertLines: { color: '#383636' }, 
        horzLines: { color: '#383636' },
      },
      crosshair: { 
        mode: 0,
        vertLine: { color: '#383636', labelBackgroundColor: '#111111' },
        horzLine: { color: '#383636', labelBackgroundColor: '#111111' }
      },
      rightPriceScale: {
        borderColor: '#111111',
        scaleMargins: { top: 0.05, bottom: 0.05 },
        mode: 0, 
      },
      timeScale: {
        borderColor: '#111111',
        timeVisible: true,
        secondsVisible: true, // Set to true since your NSE data tracks seconds
        fixLeftEdge: true, 
        fixRightEdge: true,
      },
      handleScale: {
        axisPressedMouseMove: { time: true },
      },
      handleScroll: { vertTouchDrag: false }
    });

    const fixedScaleProvider = () => ({
      priceRange: { minValue: 0, maxValue: 100 },
      margins: { above: 0, below: 0 }
    });
    

    // Set line profiles dynamically depending on chart configuration types
    const sma20Series = chart.addSeries(LineSeries, {
      color: isNscChart ? '#7faf82' : '#84AB86', // Cleaner green for advances
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma20,
    });
    seriesRefs.current.sma20 = sma20Series;

    const sma50Series = chart.addSeries(LineSeries, {
      color: isNscChart ? '#c26585' : '#C3728E', // Cleaner red for declines
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma50,
    });
    seriesRefs.current.sma50 = sma50Series;

    const sma200Series = chart.addSeries(LineSeries, {
      color: isNscChart ? '#54949b' : '#66B2BB', // Muted slate gray for unchanged
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma200,
    });
    seriesRefs.current.sma200 = sma200Series;

    chartRef.current = chart;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [isNscChart]);

  // DATA EFFECT
  useEffect(() => {
    if (!chartRef.current || !seriesRefs.current.sma20) return;

    if (Array.isArray(data) && data.length > 0) {
      const map20 = new Map();
      const map50 = new Map();
      const map200 = new Map();

      data.forEach(item => {
        let safeTimeStr = item.time;
        if (safeTimeStr && safeTimeStr.length === 10) safeTimeStr += "T00:00:00";
        else if (safeTimeStr) safeTimeStr = safeTimeStr.replace(' ', 'T'); 
        else return; 

        const dateObj = new Date(safeTimeStr);
        let timestamp = Math.floor(dateObj.getTime() / 1000);
        timestamp = timestamp - (dateObj.getTimezoneOffset() * 60);

        if (!isNaN(timestamp)) {
          // FIX: Conditional field check based on layout engine configurations
          const v20Value = isNscChart ? (item.advances_pct || 0) : (item['%above20'] || 0);
          const v50Value = isNscChart ? (item.declines_pct || 0) : (item['%above50'] || 0);
          const v200Value = isNscChart ? (item.unchange_pct || 0) : (item['%above200'] || 0);

          map20.set(timestamp, { time: timestamp, value: v20Value });
          map50.set(timestamp, { time: timestamp, value: v50Value });
          map200.set(timestamp, { time: timestamp, value: v200Value });
        }
      });

      const data20 = Array.from(map20.values()).sort((a, b) => a.time - b.time);
      const data50 = Array.from(map50.values()).sort((a, b) => a.time - b.time);
      const data200 = Array.from(map200.values()).sort((a, b) => a.time - b.time);

      if (data20.length > 0) seriesRefs.current.sma20.setData(data20);
      if (data50.length > 0) seriesRefs.current.sma50.setData(data50);
      if (data200.length > 0) seriesRefs.current.sma200.setData(data200);

      chartRef.current.timeScale().fitContent();
    } else {
      seriesRefs.current.sma20.setData([]);
      seriesRefs.current.sma50.setData([]);
      seriesRefs.current.sma200.setData([]);
    }
  }, [data, isNscChart]);

  // Crosshair Handler
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current;
    const sma20Series = seriesRefs.current.sma20;
    const sma50Series = seriesRefs.current.sma50;
    const sma200Series = seriesRefs.current.sma200;

    const handleCrosshairMove = (param) => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;

      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > chartContainerRef.current.clientWidth ||
        param.point.y < 0 ||
        param.point.y > 400
      ) {
        tooltip.style.display = 'none';
        return;
      }

      const trueUnixSeconds = param.time + (new Date().getTimezoneOffset() * 60);

      const dateStr = new Date(trueUnixSeconds * 1000).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit', // Added seconds readout formatting
        hour12: false
      });

      const v20 = param.seriesData.get(sma20Series);
      const v50 = param.seriesData.get(sma50Series);
      const v200 = param.seriesData.get(sma200Series);

      let tooltipHtml = `<div style="font-size: 11px; color: #94a3b8; margin-bottom: 4px; font-weight: bold;">${dateStr}</div>`;

      // FIX: Dynamically switch labels within tooltips based on layout type context
      if (visibility.sma20 && v20) {
        const label = isNscChart ? 'Advances' : '% > SMA 20';
        const color = isNscChart ? '#22c55e' : '#84AB86';
        tooltipHtml += `<div style="color: ${color}; font-size: 12px; font-weight: 600;">${label}: <span style="color: #fff">${v20.value.toFixed(2)}%</span></div>`;
      }
      if (visibility.sma50 && v50) {
        const label = isNscChart ? 'Declines' : '% > SMA 50';
        const color = isNscChart ? '#ef4444' : '#C3728E';
        tooltipHtml += `<div style="color: ${color}; font-size: 12px; font-weight: 600;">${label}: <span style="color: #fff">${v50.value.toFixed(2)}%</span></div>`;
      }
      if (visibility.sma200 && v200) {
        const label = isNscChart ? 'Unchanged' : '% > SMA 200';
        const color = isNscChart ? '#94a3b8' : '#66B2BB';
        tooltipHtml += `<div style="color: ${color}; font-size: 12px; font-weight: 600;">${label}: <span style="color: #fff">${v200.value.toFixed(2)}%</span></div>`;
      }

      tooltip.innerHTML = tooltipHtml;
      tooltip.style.display = 'block';

      const coordinateX = param.point.x;
      const coordinateY = param.point.y;
      
      const tooltipWidth = 150;
      const tooltipHeight = 80;
      const leftSpacing = 15;

      let left = coordinateX + leftSpacing;
      if (left > chartContainerRef.current.clientWidth - tooltipWidth) {
        left = coordinateX - tooltipWidth - leftSpacing;
      }

      let top = coordinateY + leftSpacing;
      if (top > 400 - tooltipHeight) {
        top = coordinateY - tooltipHeight - leftSpacing;
      }

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);
    return () => chart.unsubscribeCrosshairMove(handleCrosshairMove);
  }, [visibility, isNscChart]);

  const toggleVisibility = (seriesName) => {
    setVisibility(prev => ({ ...prev, [seriesName]: !prev[seriesName] }));
  };

  const resetChartView = () => {
    if (!chartRef.current) return;
    chartRef.current.timeScale().fitContent();
    chartRef.current.priceScale('right').applyOptions({
      autoScale: true,
      priceRange: { minValue: 0, maxValue: 100 },
    });
  };

  return (
    <div style={{ 
      background: '#000000', 
      border: '1px solid #111111', 
      borderRadius: '10px', 
      boxShadow: '0 18px 40px #0000002e',
      overflow: 'hidden',
      padding: '15px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ 
              margin: 0, color: '#e2e8f0', fontSize: '13px', fontWeight: '700', 
              letterSpacing: '0.06em', textTransform: 'uppercase' 
            }}>
              {timeframe}
            </h3>
            {loadingTime && (
              <span style={{ 
                fontSize: '0.7rem', color: '#4197F7', background: 'rgba(65,151,247,0.14)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(65,151,247,0.3)', fontWeight: 'bold'
              }}>
                ⏱️ {Number(loadingTime).toFixed(2)}s
              </span>
            )}
            {lastLoaded && (
              <span style={{ 
                fontSize: '0.7rem', color: '#94a3b8', background: 'rgba(148,163,184,0.1)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.2)', fontWeight: '600'
              }}>
                ↻ {lastLoaded}
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600' }}>FRAMES:</span>
            <input 
              type="number" 
              value={framesInput}
              onChange={(e) => setFramesInput(parseInt(e.target.value) || '')}
              style={{ 
                width: '60px', padding: '4px 8px', borderRadius: '6px', 
                border: '1px solid #1a1a1a', background: '#0a0a0a', color: '#e2e8f0',
                fontSize: '0.85rem', outline: 'none'
              }}
            />
            <button 
              onClick={() => onFetchFrames(framesInput)}
              style={{ 
                padding: '4px 14px', 
                background: 'linear-gradient(90deg, #4196f769 -94.84%, #c872f380 162.4%)', 
                color: '#fff', border: 'none', borderRadius: '6px', 
                cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem',
                boxShadow: '#8765f61f 0 0 0 1px'
              }}
            >
              LOAD
            </button>
          </div>
        </div>

        {/* FIX: Dynamic Legendary Controls depending on layout orientation parameters */}
        <div style={{ display: 'flex', gap: '15px', fontSize: '0.85rem', fontWeight: '600' }}>
          <span 
            onClick={() => toggleVisibility('sma20')}
            style={{ color: isNscChart ? '#22c55e' : '#84AB86', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma20 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            {isNscChart ? `Advances ${data.at(-1)?.advances || 0}` : '% > SMA 20'}
          </span>
          <span 
            onClick={() => toggleVisibility('sma50')}
            style={{ color: isNscChart ? '#ef4444' : '#C3728E', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma50 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            {isNscChart ? `Declines ${data.at(-1)?.declines || 0}` : '% > SMA 50'}
          </span>
          <span 
            onClick={() => toggleVisibility('sma200')}
            style={{ color: isNscChart ? '#94a3b8' : '#66B2BB', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma200 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            {isNscChart ? `Unchanged ${data.at(-1)?.unchange || 0}` : '% > SMA 200'}
          </span>
          <span 
            onClick={() => toggleVisibility('sma200')}
            style={{ color: isNscChart ? '#ffffff' : '#ffffff', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma200 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            {isNscChart ? `Total Stock ${data.at(-1)?.total_stocks || 0}` : '% > SMA 200'}
          </span>
        </div>
      </div>
      
      <div 
        style={{ position: 'relative', width: '100%', height: '400px', backgroundColor: '#000000', borderRadius: '8px', overflow: 'hidden', border: '1px solid #111111' }}
        onDoubleClick={resetChartView} 
      >
        <div ref={chartContainerRef} className="chart-container" style={{ width: '100%', height: '100%' }} />
        
        <div 
          ref={tooltipRef}
          style={{
            width: '160px',
            position: 'absolute',
            display: 'none',
            padding: '8px',
            boxSizing: 'border-box',
            border: '1px solid #222',
            borderRadius: '6px',
            fontFamily: 'sans-serif',
            backgroundColor: 'rgba(10, 10, 10, 0.85)',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.5)',
            textAlign: 'left',
            zIndex: 1000,
            pointerEvents: 'none', 
          }}
        />

        {(!Array.isArray(data) || data.length === 0) && (
          <div style={{ 
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            backgroundColor: 'rgba(0,0,0,0.8)', color: '#64748b', zIndex: 10,
            fontSize: '0.9rem', fontWeight: '600', letterSpacing: '0.05em'
          }}>
            {typeof data === 'string' ? data : `NO DATA AVAILABLE FOR ${timeframe.toUpperCase()}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartCard1;